import { Link } from 'react-router-dom';
import { useProjects } from '@/services/project.service';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FolderIcon, AlertCircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import DeletedBadge from '../../components/DeletedBadge';
import { BASE } from '../../basePath';
import { DEFAULT_PROJECT_ID } from '@/lib/defaultProject';

export default function ProjectList() {
    const { data: projects, isLoading, error } = useProjects();
    const { t } = useTranslation();

    return (
        <div className="max-w-5xl mx-auto px-8 py-10">
            {/* Projects are created from the backoffice. */}
            <PageHeader title={t('Projects')} />

            {isLoading && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[0, 1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-28 rounded-xl" />
                    ))}
                </div>
            )}

            {error && (
                <Alert variant="destructive">
                    <AlertCircleIcon className="h-4 w-4" />
                    <AlertTitle>{t('Error')}</AlertTitle>
                    <AlertDescription>{error.message}</AlertDescription>
                </Alert>
            )}

            {!isLoading && projects && projects.length === 0 && (
                <div className="text-center py-16 bg-white border border-dashed border-[#dadbd5] rounded-xl">
                    <FolderIcon className="h-10 w-10 text-[#cdd0c7] mx-auto mb-3" />
                    <h3 className="text-base font-semibold text-[#1c2620]">
                        {t('No projects yet')}
                    </h3>
                    <p className="text-sm text-[#7a8077] mt-1">
                        {t(
                            'Your projects will appear here once Humeos has set them up.',
                        )}
                    </p>
                </div>
            )}

            {projects && projects.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {projects.map((project) => (
                        <Link
                            key={project.id}
                            to={`${BASE}/project/${project.id}`}
                            className={`group bg-white border border-[#e3e5df] rounded-xl p-5 shadow-[0_1px_2px_rgba(28,38,32,0.05)] hover:border-[#b7cfc0] hover:shadow-[0_4px_14px_rgba(28,38,32,0.08)] transition ${
                                // Deleted projects only reach super admins.
                                project.softDelete ? 'opacity-60' : ''
                            }`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="relative h-8 w-8 rounded-lg bg-[#e8efe9] text-[#2d6a4f] flex items-center justify-center flex-shrink-0">
                                            <FolderIcon className="h-4 w-4" />
                                            {/* The default project collects
                                                unknown scanners: its samples
                                                are waiting to be sorted. */}
                                            {project.id ===
                                                DEFAULT_PROJECT_ID &&
                                                !!project.sampleCount && (
                                                    <span className="absolute -right-1.5 -top-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-[18px] text-center ring-2 ring-white tabular-nums">
                                                        {project.sampleCount}
                                                    </span>
                                                )}
                                        </div>
                                        <h3 className="text-base font-semibold text-[#1c2620] truncate">
                                            {project.name}
                                        </h3>
                                        {project.softDelete && <DeletedBadge />}
                                    </div>
                                    {project.description && (
                                        <p className="text-sm text-[#5a6259] line-clamp-2 leading-relaxed">
                                            {project.description}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
